import type { SortingState } from "@tanstack/react-table";
import type { ReactNode } from "react";
import type { Key } from "react-aria-components";
import { useLocalStorage } from "usehooks-ts";

export interface CustomFilter {
  id: string;
  label: ReactNode;
  format?: string;
  operator: string;
  value: unknown;
  overrideAccessorId?: Key;
  onValue?: string;
  offValue?: string;
  selectOptions?: { label: string; value: unknown; clearable?: boolean }[];
  override: boolean;
  min?: string | null;
  max?: string | null;
  filterFunction?: (row: unknown, value: unknown) => boolean;
}

interface TableSettings {
  tableFilters?: CustomFilter[];
  tableSorting?: SortingState;
  tablePageSize?: number;
  tableColOrder?: Record<string, number>;
}

type TablesSettings = Record<string, TableSettings>;

const useTableSettings = <T extends keyof TableSettings>(
  tableId: string,
  settingKey: T,
  defaultSettings: TableSettings[T],
): {
  savedTableSettings: TableSettings[T];
  updateTableSettings: (updatedValue: TableSettings[T]) => void;
} => {
  const [savedTableSettings, setSavedTableSettings] = useLocalStorage<TablesSettings>("tableSettings", {});

  const updateTableSettings = (updatedValue: unknown) => {
    setSavedTableSettings((oldSettings) => ({
      ...oldSettings,
      [tableId]: {
        ...oldSettings[tableId],
        [settingKey]: updatedValue,
      },
    }));
  };

  return {
    savedTableSettings: savedTableSettings[tableId]?.[settingKey] ?? defaultSettings,
    updateTableSettings,
  };
};

export default useTableSettings;
